import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { Button, Form, Modal } from 'semantic-ui-react';
import InputMask from "react-input-mask";
import { useDidUpdate, useToggle } from '../../lib/hooks';
import { Input } from '../../lib/custom-ui';
import { utcToLocalDatePicker, localDatePickerToUtc, formatMoscowTime, parseMoscowTime } from '../../utils/moscowTime';

import { useForm } from '../../hooks';

import styles from './TaskCloseDateStep.module.scss';

const TaskCloseDateStep = React.memo(({ defaultValue, onApply, onClose }) => {
  const [t] = useTranslation();
  const datePickerDateFormat = t('format:datePickerDate', { defaultValue: 'yyyy.MM.dd' });
  const datePickerDateTimeFormat = t('format:datePickerDateTime', {
    defaultValue: 'yyyy.MM.dd HH:mm',
  });
  
  const [data, handleFieldChange, setData] = useForm(() => {
    // defaultValue приходит с сервера в UTC
    let date = defaultValue;
    if (!date) {
      // Если даты нет, создаем текущую дату в МСК
      const now = new Date();
      date = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        12, 0, 0, 0
      ));
    }
    // Форматируем в МСК времени для отображения
    return {
      date: formatMoscowTime(date, datePickerDateFormat),
      time: formatMoscowTime(date, 'HH:mm'),
    };
  });

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    // Парсим дату как МСК время
    const dateStr = `${data.date} ${data.time || '00:00'}`;
    const utcDate = parseMoscowTime(dateStr);
    
    if (utcDate && !Number.isNaN(utcDate.getTime())) {
      // Конвертируем UTC в дату для datePicker (показывать как МСК)
      return utcToLocalDatePicker(utcDate);
    }

    return null;
  }, [data.date, data.time]);

  const handleDatePickerChange = useCallback(
    (date) => {
      // date приходит из react-datepicker (локальное время браузера)
      // Конвертируем в МСК время, затем в UTC для отправки на сервер
      const utcDate = localDatePickerToUtc(date);
      setData((prevData) => ({
        ...prevData,
        date: formatMoscowTime(utcDate, datePickerDateFormat),
      }));
      selectTimeField();
    },
    [setData, selectTimeField, datePickerDateFormat],
  );

  const handleApply = useCallback(() => {
    if (!nullableDate) {
      dateField.current.select();
      return;
    }

    // Парсим дату и время как МСК время, конвертируем в UTC для отправки на сервер
    const dateStr = `${data.date} ${data.time}`;
    const value = parseMoscowTime(dateStr);

    if (!value || Number.isNaN(value.getTime())) {
      timeField.current.select();
      return;
    }

    // Отправляем UTC дату на сервер
    onApply(value);
    onClose();
  }, [onApply, onClose, data, nullableDate]);

  useEffect(() => {
    dateField.current.select();
  }, []);

  useDidUpdate(() => {
    if(timeField.current)
      timeField.current.select();
  }, [selectTimeFieldState]);

  return (
    <>
      <Modal.Header>Выбор даты и времени закрытия задачи</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleApply}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.date')} (МСК)</div>
              <Input ref={dateField} name="date" value={data.date} onChange={handleFieldChange} />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.time')} (МСК)</div>
              <InputMask
                mask="99:99"
                placeholder="__:__"
                value={data.time}
                onChange={(e) => handleFieldChange(null, { name: "time", value: e.target.value })}
              >
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
          <Button positive content="Применить" onClick={handleApply} />
        </Form>
      </Modal.Content>
    </>
  );
});

TaskCloseDateStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

TaskCloseDateStep.defaultProps = {
  defaultValue: undefined,
};

export default TaskCloseDateStep;
